import type {
  AccessorKeyColumnDef,
  ColumnDef,
  ExpandedState,
  PaginationState,
  TableOptions,
} from "@tanstack/react-table";

import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ExpandToggle } from "./TableContent";
import type { TranslatedContent } from "./types";

import { useIsRtl } from "../hooks/useIsRtl";
import { TableState } from "../TableState";
import { Pagination } from "./Pagination";
import { TableContent } from "./TableContent";
import { TableDesktopSkeletonLoader } from "./TableDesktopSkeletonLoader";

// Inline-end scroll affordance for `scrollFrame` — transparent → opaque
// surface, ramping to solid fairly fast so table text doesn't bleed through.
// The gradient direction is inline-aware: it ramps toward the inline-end edge
// (right in LTR, left in RTL).
const SCROLL_FADE_GRADIENT_BASE = "from-transparent to-surface-primary to-80%";

export type TableDesktopProps<T> = {
  data: T[];
  pageSize?: number;
  showPagination?: boolean;
  columns: ColumnDef<T, any>[];
  globalFilter?: any;
  columnFilters?: any;
  onRetry?: () => void;
  sorting?: any;
  scaId?: string;
  translatedContent?: TranslatedContent;
  onFilterChange?: (rows: T[]) => void;
  showHorizontalScroll?: boolean;
  enableServerSidePagination?: boolean;
  enabledServerSideSorting?: boolean;
  pageCount?: number;
  totalCount?: number;
  state?: { pagination?: PaginationState; expanded?: ExpandedState };
  onPaginationChange?: (updater: any) => void;
  isLoading?: boolean;
  isError?: boolean;
  emptyComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  cellContainerClassName?: string;
  rowClassName?: string;
  className?: string;
  // Overrides the loading-skeleton row height (px) to match a taller real
  // row (e.g. a source cell with an icon and two text lines).
  skeletonRowHeight?: number;
  // Replaces the default `TableDesktopSkeletonLoader` while `isLoading`, for
  // tables whose skeleton can't be derived from the column defs alone (e.g. a
  // leading element that differs per view tab). Rendered inside the same scroll
  // frame as the real table, so the frame and pagination behave identically.
  // Unset, the default skeleton is used — existing consumers are unaffected.
  skeletonComponent?: React.ReactNode;
  // Optional row highlighting forwarded to TableContent. No-op unless both set.
  highlightedRowId?: string;
  getRowId?: (row: T) => string;
  // Optional per-row className forwarded to TableContent. No-op unless set.
  getRowClassName?: (row: T) => string;
  // Optional whole-row click forwarded to TableContent. No-op unless set.
  onRowClick?: (row: T) => void;
  // Pins the first column while the rest of the table scrolls horizontally.
  // Only takes effect when `showHorizontalScroll` is also true.
  stickyFirstColumn?: boolean;
  // Opts into `table-layout: fixed` so column widths are governed purely by
  // TanStack's computed sizes, never stretched by a cell's un-truncated
  // content. Off by default — existing consumers are unaffected.
  fixedLayout?: boolean;
  // Wraps the scroll viewport in a rounded, bordered frame (instead of the
  // table drawing its own outer border/corner radii) and shows a right-edge
  // fade affordance while there's more horizontal content to scroll to.
  // Only takes effect when `showHorizontalScroll` is also true.
  scrollFrame?: boolean;
  // Renders a trailing expand/collapse control on every row that has sub-rows,
  // forwarded to TableContent. Pair it with `getSubRows` and
  // `getExpandedRowModel` below. No-op unless set.
  expandToggle?: ExpandToggle<T>;
} & Pick<
  TableOptions<T>,
  | "getFilteredRowModel"
  | "onGlobalFilterChange"
  | "globalFilterFn"
  | "enableSorting"
  | "onSortingChange"
  | "filterFns"
  | "onColumnFiltersChange"
  // Row expansion, opt-in. TanStack folds expanded sub-rows into the same
  // `getRowModel()` list the table already renders, so they flow through the
  // existing column defs as ordinary rows — supply `getSubRows` to say where
  // the children live and `getExpandedRowModel` to actually flatten them.
  // `onExpandedChange` (with `state.expanded`) is for consumers that want to
  // own the expansion state; left unset, the table keeps it internally.
  | "getExpandedRowModel"
  | "onExpandedChange"
  | "getSubRows"
  | "getRowCanExpand"
>;

export const TableDesktop = <T,>({
  data,
  columns,
  showPagination = true,
  columnFilters,
  onRetry,
  pageSize = 10,
  globalFilter,
  sorting,
  translatedContent,
  enableSorting = true,
  onSortingChange,
  onFilterChange,
  scaId: _scaId,
  showHorizontalScroll,
  enableServerSidePagination = false,
  enabledServerSideSorting = false,
  pageCount,
  totalCount,
  state,
  onPaginationChange,
  isLoading = false,
  skeletonComponent,
  isError = false,
  emptyComponent,
  errorComponent,
  cellContainerClassName,
  rowClassName,
  className,
  skeletonRowHeight,
  highlightedRowId,
  getRowId,
  getRowClassName,
  onRowClick,
  stickyFirstColumn = false,
  fixedLayout = false,
  scrollFrame = false,
  expandToggle,
  ...filterProps
}: TableDesktopProps<T>) => {
  // Drives the sticky first column's freeze shadow — only shown once the
  // table has actually been scrolled horizontally, not at rest.
  const [isScrolled, setIsScrolled] = useState(false);
  // Drives the scrollFrame's inline-end fade — shown only while there's more
  // horizontal content to scroll toward the inline-end edge.
  const [hasMoreEnd, setHasMoreEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isRtl = useIsRtl();
  const { t } = useTranslation();

  const recomputeScrollAffordances = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // `scrollLeft` semantics differ across browsers in RTL: spec-compliant
    // browsers report 0 at the inline-start (rightmost) resting position and
    // grow negative as content scrolls toward the inline-end. Taking the
    // absolute value gives the distance scrolled away from the start in both
    // LTR (positive) and RTL (negative) so the maths below is direction-safe.
    const scrolledFromStart = Math.abs(el.scrollLeft);
    const maxScroll = el.scrollWidth - el.clientWidth;
    setIsScrolled(scrolledFromStart > 0);
    setHasMoreEnd(scrolledFromStart < maxScroll - 1);
  }, []);

  const [localPagination, setLocalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: showPagination ? pageSize : 100, // Adding 100 as the max table size of non paginated data
  });

  const pagination =
    enableServerSidePagination && state?.pagination
      ? state.pagination
      : localPagination;
  const setPagination =
    enableServerSidePagination && onPaginationChange
      ? onPaginationChange
      : setLocalPagination;

  // oxlint-disable-next-line react/incompatible-library -- TanStack Table's own API, not memoized by this component
  const table = useReactTable({
    _features: [],
    data,
    columns,
    enableSorting,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    columnResizeMode: "onChange",
    columnResizeDirection: isRtl ? "rtl" : "ltr",
    // `expanded` is only handed over when a consumer actually controls it.
    // Spreading it unconditionally would pass `expanded: undefined` for every
    // other table, overriding TanStack's own internal expansion state and
    // freezing the toggles.
    state: {
      pagination,
      globalFilter,
      sorting,
      columnFilters,
      ...(state?.expanded !== undefined && { expanded: state.expanded }),
    },
    manualPagination: enableServerSidePagination,
    manualSorting: enabledServerSideSorting,
    pageCount: enableServerSidePagination ? pageCount : undefined,
    // Keeps an expanded row's children counted against its own page, not the next.
    paginateExpandedRows: false,
    ...filterProps,
  });

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(
        table.getFilteredRowModel().rows.map((row) => row.original),
      );
    }
  }, [onFilterChange, globalFilter, table]);

  const rows = table.getRowModel().rows;
  // Error takes priority over empty (a stale successful page can still be
  // sitting in `data` while `isError` is true — see TableContent).
  const isEmptyState = !isError && rows.length === 0;
  const showStateOverlay = !isLoading && (isError || isEmptyState);
  const stateFrame = isLoading || showStateOverlay;
  const frameOnScrollViewport = scrollFrame || stateFrame;

  // Default error/empty overlays, used whenever a consumer doesn't supply its
  // own `errorComponent`/`emptyComponent`. Error copy comes from the shared
  // `clientsPage_table_error_*` keys (generic across every table), so it can
  // always render. Empty copy is inherently per-page, so it only comes from
  // `translatedContent` — if that's incomplete, no default is shown rather
  // than rendering a `TableState` with blank text.
  const defaultErrorState = (
    <TableState
      variant="error"
      title={t("clientsPage_table_error_title")}
      description={t("clientsPage_table_error_text")}
      actionLabel={t("clientsPage_data_error_action_text")}
      onAction={onRetry}
    />
  );

  // Mirrors the pre-existing `NoRecordsFound` heuristic: a non-empty `data`
  // with zero visible rows means the table itself has data but the current
  // filters produced no matches, vs. a genuinely empty `data` set.
  const isFilteredEmpty = data.length > 0;
  const emptyDescription = isFilteredEmpty
    ? translatedContent?.noResultsFoundText
    : translatedContent?.noDataFoundText;
  const defaultEmptyState =
    translatedContent?.noDataFoundTitle && emptyDescription ? (
      <TableState
        variant="empty"
        title={translatedContent.noDataFoundTitle}
        description={emptyDescription}
      />
    ) : undefined;

  // Measures the header row so the error/empty overlay (below) can start
  // right beneath it, matching the canvas's `.empty-overlay { inset: <header
  // height> ... }` — the overlay only covers the table's body region, not its
  // header.
  const [headerHeight, setHeaderHeight] = useState(0);
  const recomputeHeaderHeight = useCallback(() => {
    const theadEl = scrollRef.current?.querySelector("thead");
    if (theadEl) setHeaderHeight(theadEl.getBoundingClientRect().height);
  }, []);

  useEffect(() => {
    if (!showStateOverlay) return;
    recomputeHeaderHeight();
    const theadEl = scrollRef.current?.querySelector("thead");
    if (!theadEl) return;
    const resizeObserver = new ResizeObserver(recomputeHeaderHeight);
    resizeObserver.observe(theadEl);
    return () => resizeObserver.disconnect();
  }, [showStateOverlay, recomputeHeaderHeight]);

  // TanStack's total content width — changes as columns are resized. Observing
  // the scroll container alone won't catch this (its own box stays the same
  // size while only the inner content grows), so we key a recompute off it.
  const centerTotalSize = table.getCenterTotalSize();

  // Recompute the scrollFrame's right-edge fade whenever the viewport
  // resizes or the underlying data/columns change the scrollable width.
  useEffect(() => {
    if (!showHorizontalScroll || !scrollFrame) return;
    recomputeScrollAffordances();
    const el = scrollRef.current;
    if (!el) return;
    const resizeObserver = new ResizeObserver(recomputeScrollAffordances);
    // Observe the scroll container (viewport size changes) and its content
    // element (column resizing grows the inner width without resizing the
    // container), so the fade stays in sync during column resize interactions.
    resizeObserver.observe(el);
    if (el.firstElementChild) {
      resizeObserver.observe(el.firstElementChild);
    }
    window.addEventListener("resize", recomputeScrollAffordances);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", recomputeScrollAffordances);
    };
  }, [
    showHorizontalScroll,
    scrollFrame,
    recomputeScrollAffordances,
    data,
    columns,
    isLoading,
  ]);

  // Column resizing (columnResizeMode: "onChange") mutates the column sizing
  // state, changing getCenterTotalSize() without resizing the scroll container.
  // Recompute after the resulting layout paints so hasMoreRight never goes stale.
  useEffect(() => {
    if (!showHorizontalScroll || !scrollFrame) return;
    const frame = requestAnimationFrame(recomputeScrollAffordances);
    return () => cancelAnimationFrame(frame);
  }, [
    showHorizontalScroll,
    scrollFrame,
    recomputeScrollAffordances,
    centerTotalSize,
  ]);

  return (
    <div
      data-testid="table-desktop"
      className={`mb-10 mt-6 ${className ?? ""}`}
    >
      <div className={clsx((scrollFrame || showStateOverlay) && "relative")}>
        <div
          ref={scrollRef}
          className={clsx(
            "overflow-y-hidden",
            showHorizontalScroll
              ? "scrollbar-minimal overflow-x-auto"
              : "overflow-x-hidden",
            frameOnScrollViewport && "rounded-2xl border border-stroke-primary",
          )}
          onScroll={
            showHorizontalScroll ? recomputeScrollAffordances : undefined
          }
        >
          {isLoading ? (
            (skeletonComponent ?? (
              <TableDesktopSkeletonLoader
                columns={columns as AccessorKeyColumnDef<any, any>[]}
                pageSize={pageSize}
                filterFns={filterProps.filterFns}
                rowHeight={skeletonRowHeight}
                fixedLayout={fixedLayout}
                insideScrollFrame={frameOnScrollViewport}
              />
            ))
          ) : (
            <TableContent
              table={table}
              columns={columns}
              cellContainerClassName={cellContainerClassName}
              rowClassName={rowClassName}
              enableHorizontalScroll={showHorizontalScroll}
              stickyFirstColumn={stickyFirstColumn}
              highlightedRowId={highlightedRowId}
              getRowId={getRowId}
              fixedLayout={fixedLayout}
              isScrolled={isScrolled}
              insideScrollFrame={frameOnScrollViewport}
              getRowClassName={getRowClassName}
              onRowClick={onRowClick}
              expandToggle={expandToggle}
              isError={isError}
            />
          )}
        </div>
        {scrollFrame && showHorizontalScroll && (
          <div
            aria-hidden
            className={clsx(
              "pointer-events-none absolute bottom-4 top-px w-16 transition-opacity duration-150",
              // Pin the fade to the inline-end edge: right in LTR, left in RTL,
              // and ramp the gradient toward that same edge.
              isRtl
                ? "left-px rounded-l-[15px] bg-gradient-to-l"
                : "right-px rounded-r-[15px] bg-gradient-to-r",
              SCROLL_FADE_GRADIENT_BASE,
              hasMoreEnd ? "opacity-100" : "opacity-0",
            )}
          />
        )}
        {showStateOverlay && (
          // Sibling of the scroll viewport (not inside it), so it stays put
          // under horizontal scroll instead of drifting with the scrolled
          // table — the pre-fix bug was rendering this inside the scrollable
          // `tbody`, where its centering tracked the full scrolled width
          // rather than the visible viewport.
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center"
            style={{ top: headerHeight }}
          >
            <div
              className="pointer-events-auto w-full"
              // Keep the legacy testid on the default error overlay so
              // existing tests and e2e page objects don't need updating.
              {...(isError && !errorComponent
                ? { "data-testid": "api-error-table" }
                : {})}
            >
              {isError
                ? (errorComponent ?? defaultErrorState)
                : (emptyComponent ?? defaultEmptyState)}
            </div>
          </div>
        )}
      </div>
      {showPagination && table.getPageCount() > 1 && (
        <>
          <div className="h-10" />
          <Pagination
            table={table}
            pageSize={pageSize}
            labelForShowing={translatedContent?.paginationLabelForShowing}
            labelForOf={translatedContent?.paginationLabelForOf}
            isRightToLeft={isRtl}
            totalCount={totalCount}
          />
        </>
      )}
    </div>
  );
};
